import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAccount, useNetwork, useWaitForTransaction, useContractRead, useContractWrite } from 'wagmi';
import BigNumber from 'bignumber.js';
import { useCookies } from 'react-cookie';

import { NETWORK, ADDRESSES, ABIS } from "../config";

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SwitchNetwork } from '../components/SwitchNetwork';
import SessionHandler from '../components/SessionHandler';

import { LP_STAKING_OFFERS } from '../stakingOffers';

import eclipseSpinner from "../images/EclipseSpinner.png";



function addLeadingZero(num) {
  num = num.toString();
  if (num.length < 2) {
    num = "0" + num;
  }
  return num;
}

function convertDate(timestamp) {
  let myDate = new Date(timestamp);
  let dateString = myDate.getFullYear() + "-" + addLeadingZero(myDate.getMonth() + 1) + "-" + addLeadingZero(myDate.getDate());

  return dateString;
}

function convertDecimals(input) {
  return BigNumber(input) / 10 ** 18;
}

function convertToDecimals(input) {
  return BigNumber(input) * 10 ** 18;
}

function convertBioFiAmount(amount, fullAmount) {
  let digits = 0;
  if (fullAmount !== undefined && fullAmount === true) digits = 6;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
  });
  let convertedString = formatter.format(amount);

  return convertedString.slice(1);
}

export default function StakeLP({ validChain }) {

  const [userAccountType, setUserAccountType] = useState('-');

  const [userLPBalanceInt, setUserLPBalanceInt] = useState(0);
  const [userLPBalance, setUserLPBalance] = useState('0');
  const [stakeDisplayData, setStakeDisplayData] = useState(null);
  const [userHasActiveStake, setUserHasActiveStake] = useState(null);
  const [stakingScreenActive, setStakingScreenActive] = useState(false);
  const [stakingPopupActive, setStakingPopupActive] = useState(false);
  const [stakeAmount, setStakeAmount] = useState(0);
  const [tocChecked, setTocChecked] = useState(false);
  const [processStatus, setProcessStatus] = useState('Loading...');


  const [cookies] = useCookies(['userType']);

  let { stakeId } = useParams();

  const navigate = useNavigate();

  const { address } = useAccount();
  let isWalletConnected = (address !== null && typeof (address) !== 'undefined');

  const { chain } = useNetwork();
  let isNetworkCorrect = chain?.id === validChain?.id;

  useEffect(() => {
    let userType = cookies.userType;
    if (userType === undefined) {
      userType = "user";
    }
    setUserAccountType(userType);
  }, []);


  const useReadUserBalance = useContractRead({
    address: ADDRESSES[NETWORK.id].biofiLP,
    abi: ABIS.biofiLP,
    functionName: "balanceOf",
    enabled: address !== null && typeof (address) !== 'undefined',
    args: [address],
    watch: false,
  });

  const useReadUserStake = useContractRead({
    address: ADDRESSES[NETWORK.id].lpStaking,
    abi: ABIS.lpStaking,
    functionName: "readStakes",
    enabled: address !== null && typeof (address) !== 'undefined',
    args: [address, 0],
    watch: false,
  });


  const useWriteApprove = useContractWrite({
    address: ADDRESSES[NETWORK.id].biofiLP,
    abi: ABIS.biofiLP,
    functionName: "approve",
    onError(error) {
      console.log('Error', error)
      toast.error("Adding tokens failed - try again");
      closeStakePopup();
    },
    onSuccess(data) {
      console.log('topUpStake tx submitted', data);
      toast("Transaction pending...");
      setProcessStatus("Waiting for the transaction to be confirmed on-chain");
      const txResult = waitForApproval;
      console.log(txResult);
    },
  });

  const useWriteCreateStake = useContractWrite({
    address: ADDRESSES[NETWORK.id].lpStaking,
    abi: ABIS.lpStaking,
    functionName: "createStake",
    onError(error) {
      console.log('Error', error)
      toast.error("Deposit failed - try again");
      closeStakePopup();
    },
    onSuccess(data) {
      console.log('createStake tx submitted', data);
      toast("Transaction pending...");
      setProcessStatus("Waiting for the transaction to be confirmed on-chain");
      const txResult = waitForCreateStake;
      console.log(txResult);
    },
  });

  const waitForApproval = useWaitForTransaction({
    hash: useWriteApprove.data?.hash,
    onSuccess(data) {
      toast.success("Approved successfully");
      console.log('Approved successfully', data);
      createStake();
    },
  });

  const waitForCreateStake = useWaitForTransaction({
    hash: useWriteCreateStake.data?.hash,
    onSuccess(data) {
      toast.success("Deposited successfully");
      console.log('Deposited successfully', data);
      setProcessStatus("Deposit successful! You'll be redirected to the dashboard now.");
      setTimeout(function () {
        navigate('/dashboard');
      }, 2000);
    },
  });

  function formatInteger(n) {
    let string = String(n);
    if (string.includes('.')) {
      let parts = string.split('.');
      let first = parts[0].replace(/(.)(?=(\d{3})+$)/g, '$1,');
      let result = first + '.' + parts[1];
      return result;
    }
    return String(n).replace(/(.)(?=(\d{3})+$)/g, '$1,');
  }

  async function fetchUserBalance() {
    console.log('fetchUserBalance');
    if (useReadUserBalance.data === undefined) return;

    let userBalance = BigNumber(useReadUserBalance.data.toString()).dividedBy(10 ** 18);
    let convertedUserBalance = convertBioFiAmount(userBalance, true);

    setUserLPBalance(convertedUserBalance.toString());

    console.log(userBalance.toFixed());
    console.log(userBalance.toFixed() > 10000);
    setUserLPBalanceInt(userBalance.toFixed());

  }


  async function fetchUserStake() {
    console.log('fetchUserStake');
    if (useReadUserStake.data === undefined) return;
    if (userHasActiveStake !== null) return;

    let stakeInfo = useReadUserStake.data;
    console.log("stakeInfo", stakeInfo);

    if (stakeInfo.exists === true) {
      toast.error("You already have an active rewards contract. Visit the dashboard for more details.");
    }

    setUserHasActiveStake(stakeInfo.exists);

  }

  useEffect(() => {
    fetchUserBalance();
  }, [useReadUserBalance.data]);

  useEffect(() => {
    fetchUserStake();
  }, [useReadUserStake.data]);

  useEffect(() => {
    let newDisplayData = LP_STAKING_OFFERS[stakeId];
    if(newDisplayData === undefined) {
      navigate("/biofiRewards");
    }
    setStakeDisplayData(newDisplayData);
  }, [stakeId]);


  function revealRewardsSection() {
    console.log("revealRewardsSection");
    setStakingScreenActive(true);
  }

  function startStakingProcess() {
    console.log("startStakingProcess");
    setStakingPopupActive(true);
    approveTokens();
  }

  function closeStakePopup() {
    console.log("closeStakePopup");
    setStakingPopupActive(false);
  }


  async function approveTokens() {
    if (stakeAmount === undefined || stakeAmount <= 0) return;

    toast("Approve LP spending...");

    let amount = convertToDecimals(stakeAmount);

    try {
      useWriteApprove.write({
        args: [ADDRESSES[NETWORK.id].lpStaking, amount]
      });
      setProcessStatus("Confirm the approval tx in your wallet");
    } catch (e) {
      console.error(e);
    }

  }

  async function createStake() {
    if (stakeAmount === undefined || stakeAmount <= 0) return;

    toast("Confirm deposit transaction");

    let amount = convertToDecimals(stakeAmount);

    try {
      useWriteCreateStake.write({
        args: [stakeDisplayData?.StakingOfferIndex, amount]
      });
      setProcessStatus("Confirm the deposit tx in your wallet");
    } catch (e) {
      console.error(e);
    }

  }
  return (
    <>
      <Navbar validChain={validChain} />
      {
        isNetworkCorrect ? (
          <>

            <div>
              <div id="section2" className="bf-csSect2" style={{ display: 'block' }}>
                <div className="bf-csSect3">
                  <h3 className="bf-hdr2"> Details of this $BioFi Rewards Offer </h3>
                  <div id="balanceInput" className="bf-input2 user-balance-wrapper" >{userLPBalance} BioFi-WBNB LP</div>
                  <Link to="/dashboard" id="dashboardButton" className="bf-stk-btn1"> Enter Dashboard </Link>
                </div>
              </div>
              {
                !stakingScreenActive ? (
                  <div id="rewardsSelectionDetails" className="bf-prevMain1" style={{ display: 'block' }}>
                    <div className="bf-flex-center">
                      <div className="bf-prevcont">
                        <div className="bf-prevsect">
                          <span id="StakingCategory" className="bf-prvtxt1" style={{ color: stakeDisplayData?.StakingColor }}><b> {stakeDisplayData?.StakingCategory} </b></span> Tier
                        </div>
                        <div className="bf-row-horizontal">
                          <div className="bf-splitContainer">
                            <h4 style={{ marginBottom: '5px' }}>Financials</h4>
                            <ul className="bf-prvLI" style={{ color: 'lightsteelblue' }}>
                              <li>
                                Annual Reward: 
                                <b><span id="AnnualReward">{stakeDisplayData?.AnnualReward}</span></b>
                              </li>
                            </ul>
                          </div>
                          <div className="bf-splitContainer">
                            <h4>Rewards &amp; Perks</h4>
                            <ul style={{ color: 'lightsteelblue' }} id="StakingRewards">
                              {stakeDisplayData?.StakingRewards.map(function (data, index) {
                                return (
                                  <li key={index}>
                                    <a href={data[1]} target="_blank" rel="noreferrer">{data[0]}</a>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        </div>
                        <div className="bf-row">
                          <div style={{ paddingRight: '15px', paddingLeft: '15px', textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                            <h4> Utility Usage </h4>
                            <ul style={{ color: 'lightsteelblue' }}>
                              <li className="bf-prvLI">Utility usage period: <b id="UtilityUsagePeriod">{stakeDisplayData?.UtilityUsagePeriod}</b></li>
                            </ul>
                            <button id="revealRewardsProcessButton" onClick={revealRewardsSection} className="bf-btn3" disabled={(userHasActiveStake !== null && userHasActiveStake === true)}> Next </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                ) : (
                  <>
                    <div id="rewardsProcessSection">
                      <div className="bf-prevcont">
                        <div className="bf-rwdContainer">
                          <h4 style={{ color: 'skyblue' }}>The LP Rewards Process</h4>
                          <span>Before you can deposit your Uniswap BioFi-WBNB LP tokens, make sure to add liquidity to the <Link to="https://app.uniswap.org/positions/create/v2/" target="_blank">Uniswap V2</Link> pool (video guide coming soon).</span>
                          <br/><br/>
                          The rewards process involves the following steps, which will take several minutes to complete, start to finish.<br />
                          You will need to have the required LP balance available prior to starting the process.
                          <div className="bf-prvListCont">
                            <ul>
                              <li className="bf-prvList">The LP smart contract is prepared for your rewards usage by allocating the required number of LP tokens.</li>
                              <li className="bf-prvList">Wallet asks you to preapprove the transfer of the required number of LP tokens to the BioFi rewards smart contract.</li>
                              <li className="bf-prvList">Wallet asks you to approve the transaction. The LP will not be transferred until you approve the confirmation step.</li>
                              <li className="bf-prvList">You are notified when the LP token transfer complete.</li>
                            </ul></div>
                        </div>
                        <hr style={{ marginBottom: '20px' }} />
                        <h4 style={{ color: 'skyblue' }}>Setup LP Deposit</h4>
                        <div className="bf-inside-table">
                          <div className="bf-trowclr">
                            <div className="bf-prvLeftCol">LP Amount</div>
                            <div className="bf-prvRightCol">
                              <input id="biofiStakingAmount" type="number" className="bf-stakeAmtTxt" value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)} />
                            </div>
                          </div>
                          <div className="bf-trowclr">
                            <div className="bf-prvLeftCol"> 
                            <div className="bf-prvcheck"><input type="checkbox" checked={tocChecked} onChange={(e) => setTocChecked(!tocChecked)} /></div> 
                            <Link to="/terms" target="_blank" style={{ color: 'skyblue', marginLeft: '10px' }}> I have read the BioFi terms and conditions</Link></div>
                            <div className="bf-prvRightCol">
                              <button id="startStakingButton" className="bf-btn3a" onClick={startStakingProcess} disabled={!tocChecked || parseFloat(stakeAmount) <= 0}> Start Rewards Process </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>

                )
              }


              {
                stakingPopupActive ? (
                  <div id="stakingProgressPopup" className="bf-stakingProgressPopup">
                    <div className="bf-innerStakingProgress">
                      <button className="close bf-popclose button-link" id="closePopup" style={{ position: 'absolute', right: '1rem', top: '.5rem' }} onClick={closeStakePopup}>x</button>
                      <h2 className="bf-csHdrSetup">LP Rewards Setup Process</h2>
                      <hr style={{ backgroundColor: 'white', marginTop: '.5rem' }} />
                      <p style={{ position: 'relative', padding: '0 25px', color: '#ffffff', fontSize: '.9rem' }}>
                        You are allocating <b><span id="biofiStakeAmountInPopup" style={{ color: 'rgb(255, 255, 255)' }}>{stakeAmount}</span> <span style={{ color: 'rgb(255, 255, 255)' }}>LP</span></b>. The LP rewards setup process normally takes from 2 to 5 minutes, depending on the amount of congestion on the blockchain. You can track the progress on <a href={"https://bscscan.com/address/" + address} style={{ color: 'rgb(221, 255, 0)', fontSize: 'inherit' }} target="_blank" rel="noreferrer">BscScan</a>.
                        <br /><br />
                        You will have to approve two transactions in your wallet to complete the rewards setup process. If you want to back out, click the Reject Button in your wallet and close this window by clicking the "X" in the upper-right-hand corner.
                        <br />
                      </p>
                      <div className="bf-gearDiv"><img id="spinningGear" src={eclipseSpinner} className="bf-gearHead" /></div>

                      <span className="staking-status">{processStatus}</span>
                      
                      <img id="confetti" src="images/check-mark-verified.gif" style={{ position: 'absolute', top: '70px', bottom: 0, left: 0, right: 0, opacity: '0.6', width: '100%', visibility: 'hidden' }} />
                    </div>
                  </div>
                ) : null
              }
            </div>
          </>
        ) : (
          <SwitchNetwork validChain={validChain} />
        )
      }

      <Footer />
      <SessionHandler />
    </>
  );
}